import { useState, type FormEvent } from "react";
import { changePassword } from "../../services/adminSession";
import { useI18n } from "../../i18n/localization";
import { useAdminSession } from "./AdminSessionGate";
import { passwordChangeErrorMessage } from "./authMessages";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, validatePasswordChange } from "./passwordChange";

// Admin Settings — change the shared administrator password. Requires the current password;
// the server re-validates the policy, stores only a salted hash, and returns a fresh session
// (all other admin sessions are signed out). Fields are cleared after every attempt.
export function AdminSettings() {
  const { t } = useI18n();
  const { session, replaceSession, guard } = useAdminSession();
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNext] = useState("");
  const [confirmPassword, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const clear = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    const invalid = validatePasswordChange({ currentPassword, newPassword, confirmPassword });
    if (invalid) {
      setError(t.settings.errors[invalid]);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      replaceSession(await guard(changePassword(session, currentPassword, newPassword, confirmPassword)));
      setSuccess(true);
    } catch (err) {
      setError(passwordChangeErrorMessage(err, t));
    } finally {
      clear();
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="admin-settings-password" style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 18, background: "#fff", maxWidth: 460 }}>
      <form onSubmit={submit} noValidate>
        <h2 id="admin-settings-password" style={{ fontSize: 15, color: "#14305f", margin: "0 0 6px" }}>{t.settings.passwordTitle}</h2>
        <p id="admin-settings-password-hint" style={{ color: "#64748b", fontSize: 13, margin: "0 0 14px" }}>{t.settings.passwordHint}</p>
        <PasswordField label={t.settings.current} value={currentPassword} onChange={setCurrent}
          autoComplete="current-password" invalid={!!error} />
        <PasswordField label={t.settings.next} value={newPassword} onChange={setNext}
          autoComplete="new-password" invalid={!!error} describedBy="admin-settings-password-hint" />
        <PasswordField label={t.settings.confirm} value={confirmPassword} onChange={setConfirm}
          autoComplete="new-password" invalid={!!error} />
        <div aria-live="polite" style={{ minHeight: 18, marginBottom: 10, fontSize: 13 }}>
          {error && <div role="alert" style={{ color: "#b91c1c" }}>{error}</div>}
          {success && !error && <div role="status" style={{ color: "#15803d" }}>{t.settings.success}</div>}
        </div>
        <button type="submit" disabled={busy} aria-busy={busy}
          style={{ background: busy ? "#94a3b8" : "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", cursor: busy ? "default" : "pointer" }}>
          {busy ? t.settings.saving : t.settings.submit}
        </button>
      </form>
    </section>
  );
}

function PasswordField({ label, value, onChange, autoComplete, invalid, describedBy }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: "current-password" | "new-password";
  invalid: boolean;
  describedBy?: string;
}) {
  return (
    <label style={{ display: "block", color: "#14305f", fontSize: 13, marginBottom: 12 }}>
      {label}
      <input type="password" value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete}
        maxLength={PASSWORD_MAX_LENGTH} minLength={autoComplete === "new-password" ? PASSWORD_MIN_LENGTH : undefined}
        dir="ltr" required aria-invalid={invalid} aria-describedby={describedBy}
        style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", border: "1px solid #d6deea", borderRadius: 6, boxSizing: "border-box" }} />
    </label>
  );
}
